import React, { useCallback, useEffect, useState } from 'react';
import Input from './Input';
import Button from './Button';
import { IoSearchOutline } from 'react-icons/io5';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Skeleton } from './Skeleton';
import { Base_url } from '../utils/Base_url';

const RunningCaseDepositsPage = ({ Layout, backPath }) => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [activeDeposits, setActiveDeposits] = useState([]);
  const [closedDeposits, setClosedDeposits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredActive, setFilteredActive] = useState([]);
  const [filteredClosed, setFilteredClosed] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [profit, setProfit] = useState('');
  const [loss, setLoss] = useState('');
  const [deduction, setDeduction] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${Base_url}/v1/worker/running-enrollement/${enrollmentId}/deposits`
      );
      const result = await response.json();

      if (response.ok && result.success) {
        setEnrollment(result.enrollment);
        setActiveDeposits(result.activeDeposits || []);
        setClosedDeposits(result.closedDeposits || []);
        setSummary(result.summary || null);
      } else {
        toast.error(result.message || 'Failed to load deposits');
        navigate(backPath);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load deposits');
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  }, [enrollmentId, backPath, navigate]);

  useEffect(() => {
    if (enrollmentId) fetchDeposits();
  }, [enrollmentId, fetchDeposits]);

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const filterList = (list) => {
      if (!term) return list;
      return list.filter((item) => {
        return (
          String(item.amount).includes(term) ||
          item?.depositType?.toLowerCase().includes(term) ||
          item?.receiptNumber?.toLowerCase().includes(term) ||
          item?.reference?.toLowerCase().includes(term)
        );
      });
    };
    setFilteredActive(filterList(activeDeposits));
    setFilteredClosed(filterList(closedDeposits));
    setCurrentPage(1);
  }, [activeDeposits, closedDeposits, searchTerm]);

  const currentPageData = filteredActive.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);

  const openCloseModal = (deposit) => {
    setSelectedDeposit(deposit);
    setProfit('');
    setLoss('');
    setDeduction('');
    setErrorMessage('');
    setIsCloseModalOpen(true);
  };

  const closeModal = () => {
    setIsCloseModalOpen(false);
    setSelectedDeposit(null);
    setIsSubmitting(false);
    setErrorMessage('');
  };

  const handleCloseDeposit = async () => {
    if (!profit.trim() && !loss.trim()) {
      setErrorMessage('Either profit or loss is required');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(
        `${Base_url}/v1/worker/close-deposit/${selectedDeposit._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            enrollmentId,
            profit: profit.trim() ? parseFloat(profit) : 0,
            loss: loss.trim() ? parseFloat(loss) : 0,
            deduction: deduction.trim() ? parseFloat(deduction) : 0
          })
        }
      );
      const result = await response.json();

      if (response.ok && result.success) {
        closeModal();
        if (result.enrollmentCompleted) {
          toast.success('All deposits closed. Enrollment moved to Completed.');
          navigate(backPath);
          return;
        }
        toast.success('Deposit closed successfully');
        fetchDeposits();
      } else {
        setErrorMessage(result.message || 'Failed to close deposit');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const investorName = enrollment?.investorName || 'Investor';
  const planName = enrollment?.planId?.planName || 'Plan';

  return (
    <>
      <Layout
        language={<p className="sm:block hidden">English | Urdu</p>}
        ToggleBtn={
          <div className="flex gap-4 items-center">
            <HiOutlineArrowLeft
              className="cursor-pointer hover:text-primary transition-colors"
              onClick={() => navigate(backPath)}
            />
            <h2 className="text-xl font-semibold">
              Running Case Deposits{' '}
              <span className="text-darkGray font-medium text-sm">/ {investorName}</span>
            </h2>
          </div>
        }
        title_props={
          <div className="w-96 relative">
            <Input
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search deposits..."
              className="border bg-white w-full rounded-full"
            />
            <Button
              Icon={<IoSearchOutline className="text-white text-xl" />}
              label="search"
              className="absolute top-0 right-0 bg-secondary text-white uppercase font-normal rounded-full"
            />
          </div>
        }
      >
        <div className="mb-6">
          <button
            onClick={() => navigate(backPath)}
            className="flex items-center gap-2 text-secondary hover:text-primary transition-colors"
          >
            <HiOutlineArrowLeft />
            <span>Back to Running Cases</span>
          </button>
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-500">Loading deposits...</div>
        ) : (
          <>
            {summary && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 mb-6 border border-blue-100">
                <h3 className="text-lg font-semibold mb-2 text-gray-800">{planName}</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Close deposits one by one. Enrollment moves to Completed only when all deposits
                  are closed.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <p className="text-sm text-gray-500">Total Deposits</p>
                    <p className="font-bold text-2xl text-gray-800">{summary.totalDeposits}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="font-bold text-2xl text-amber-600">{summary.activeDepositsCount}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <p className="text-sm text-gray-500">Closed</p>
                    <p className="font-bold text-2xl text-green-600">{summary.closedDepositsCount}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <p className="text-sm text-gray-500">Closed Profit</p>
                    <p className="font-bold text-xl text-green-700">
                      Rs. {formatCurrency(summary.totalClosedProfit)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="block rounded-lg bg-white shadow mb-8">
              <div className="px-6 py-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-800">Pending Deposits</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full mb-0">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-darkGray">Date</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray">Amount</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray">Type</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray">Receipt</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray whitespace-nowrap min-w-[120px]">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageData.length > 0 ? (
                      currentPageData.map((deposit) => (
                        <tr key={deposit._id} className="border-b hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            {moment(deposit.depositDate).format('DD/MM/YYYY')}
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            Rs. {formatCurrency(deposit.amount)}
                          </td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">{deposit.depositType}</td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">{deposit.receiptNumber}</td>
                          <td className="px-6 py-4 text-center whitespace-nowrap">
                            <button
                              onClick={() => openCloseModal(deposit)}
                              className="bg-secondary hover:bg-primary text-white px-4 py-2 rounded-md text-sm transition-colors shadow-sm"
                            >
                              Close
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-8 text-gray-500">
                          No pending deposits
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {filteredActive.length > itemsPerPage && (
                <div className="flex justify-between items-center py-4 px-6 border-t bg-gray-50">
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {Math.ceil(filteredActive.length / itemsPerPage)}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      className="bg-secondary text-white w-9 h-9 rounded-md disabled:opacity-40"
                    >
                      <FaAngleLeft className="mx-auto" />
                    </button>
                    <button
                      disabled={currentPage * itemsPerPage >= filteredActive.length}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      className="bg-secondary text-white w-9 h-9 rounded-md disabled:opacity-40"
                    >
                      <FaAngleRight className="mx-auto" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="block rounded-lg bg-white shadow">
              <div className="px-6 py-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-800">Closed Deposits</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full mb-0">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-darkGray">Date</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray">Amount</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray">Profit</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray">Loss</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray">Deduction</th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-darkGray">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClosed.length > 0 ? (
                      filteredClosed.map((deposit) => {
                        const net =
                          (deposit.amount || 0) +
                          (deposit.profit || 0) -
                          (deposit.loss || 0) -
                          (deposit.deduction || 0);
                        return (
                          <tr key={deposit._id} className="border-b hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              {moment(deposit.depositDate).format('DD/MM/YYYY')}
                            </td>
                            <td className="px-6 py-4 text-center whitespace-nowrap">
                              Rs. {formatCurrency(deposit.amount)}
                            </td>
                            <td className="px-6 py-4 text-center text-green-600 whitespace-nowrap">
                              +Rs. {formatCurrency(deposit.profit)}
                            </td>
                            <td className="px-6 py-4 text-center text-red-600 whitespace-nowrap">
                              -Rs. {formatCurrency(deposit.loss)}
                            </td>
                            <td className="px-6 py-4 text-center text-red-600 whitespace-nowrap">
                              -Rs. {formatCurrency(deposit.deduction)}
                            </td>
                            <td className="px-6 py-4 text-center font-semibold whitespace-nowrap">
                              Rs. {formatCurrency(net)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-8 text-gray-500">
                          No closed deposits yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </Layout>

      {isCloseModalOpen && selectedDeposit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Close Deposit</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg text-sm space-y-2">
                <p>
                  <span className="text-gray-500">Amount:</span>{' '}
                  <strong>Rs. {formatCurrency(selectedDeposit.amount)}</strong>
                </p>
                <p>
                  <span className="text-gray-500">Date:</span>{' '}
                  <strong>{moment(selectedDeposit.depositDate).format('DD/MM/YYYY')}</strong>
                </p>
                <p>
                  <span className="text-gray-500">Receipt:</span>{' '}
                  <strong>{selectedDeposit.receiptNumber}</strong>
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profit</label>
                  <Input
                    type="number"
                    value={profit}
                    onChange={(e) => setProfit(e.target.value)}
                    placeholder="Enter profit"
                    className="w-full border-gray-300 rounded-md"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Loss</label>
                  <Input
                    type="number"
                    value={loss}
                    onChange={(e) => setLoss(e.target.value)}
                    placeholder="Enter loss"
                    className="w-full border-gray-300 rounded-md"
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deduction</label>
                  <Input
                    type="number"
                    value={deduction}
                    onChange={(e) => setDeduction(e.target.value)}
                    placeholder="Enter deduction"
                    className="w-full border-gray-300 rounded-md"
                    disabled={isSubmitting}
                  />
                </div>

                {errorMessage && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{errorMessage}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={closeModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCloseDeposit}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm text-white bg-secondary hover:bg-primary rounded-md disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <Skeleton className="skeleton-bone-soft h-4 w-20 rounded-md bg-transparent" />
                  ) : (
                    'Close Deposit'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RunningCaseDepositsPage;
